import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import API_BASE_URL from '../config';
import { addToCart } from '../store/cartSlice';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [toast, setToast] = useState(null);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const brandsContainerRef = useRef(null);
  const autoPlayRef = useRef(null);

  const heroSlides = [
    { id: 1, image: '/assets/hero1.png' },
    { id: 2, image: '/assets/hero2.png' },
    { id: 3, image: '/assets/hero3.png' },
    { id: 4, image: '/assets/hero4.png' },
    { id: 5, image: '/assets/hero5.png' },
  ];

  const defaultBrands = [
    { id: 1,  name: 'Ray-Ban',       logo: '/assets/brands/rayban-logo.jpg',      alt: 'Ray-Ban Logo' },
    { id: 2,  name: 'Crizal',        logo: '/assets/brands/crizal-logo.jpg',       alt: 'Crizal Logo' },
    { id: 3,  name: 'Zeiss',         logo: '/assets/brands/zeiss-logo.jpg',        alt: 'Zeiss Logo' },
    { id: 4,  name: 'Hoya',          logo: '/assets/brands/hoya-logo.jpg',         alt: 'Hoya Logo' },
    { id: 5,  name: 'Varilux',       logo: '/assets/brands/varilux-logo.jpg',      alt: 'Varilux Logo' },
    { id: 6,  name: 'Vogue',         logo: '/assets/brands/vogue-logo.jpg',        alt: 'Vogue Logo' },
    { id: 7,  name: 'Essilor',       logo: '/assets/brands/essilor-logo.jpg',      alt: 'Essilor Logo' },
    { id: 8,  name: 'Bausch & Lomb', logo: '/assets/brands/bausch-lomb-logo.jpg',  alt: 'Bausch & Lomb Logo' },
    { id: 9,  name: 'Nova',          logo: '/assets/brands/nova-logo.jpg',         alt: 'Nova Logo' },
    { id: 10, name: 'Kodak',         logo: '/assets/brands/kodak-logo.jpg',        alt: 'Kodak Logo' },
  ];

  const testimonials = [
    { id: 1, name: 'Sarah Johnson',   rating: 5, comment: '"The quality of these frames is exceptional. I receive compliments everywhere I go."',  verified: true },
    { id: 2, name: 'Michael Chen',    rating: 5, comment: '"Fast shipping and perfect fit. The prescription accuracy is spot on."',                verified: true },
    { id: 3, name: 'Emily Rodriguez', rating: 5, comment: '"Customer service helped me find the perfect frames for my face shape."',               verified: true },
    { id: 4, name: 'David Kumar',     rating: 5, comment: '"Amazing collection and great prices. Will definitely shop again!"',                    verified: true },
    { id: 5, name: 'Priya Sharma',    rating: 5, comment: '"The virtual try-on feature is a game changer. Found my perfect pair!"',                verified: true },
    { id: 6, name: 'James Wilson',    rating: 5, comment: '"Excellent customer support and quick resolution of my query."',                        verified: true },
  ];

  // Get product image URL - handles all possible data structures
  const getProductImageUrl = (images) => {
    if (!images) return 'https://via.placeholder.com/400x400/f5f5f5/999999?text=No+Image';
    if (Array.isArray(images) && images.length > 0) {
      const img = images[0];
      if (typeof img === 'string') return img;
      if (img.image_url) return img.image_url;
      if (img.url) return img.url;
      if (img.src) return img.src;
    }
    if (typeof images === 'string') return images;
    return 'https://via.placeholder.com/400x400/f5f5f5/999999?text=No+Image';
  };

  // Resolve category image URL
  const resolveImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/400x400/f5f5f5/999999?text=No+Image';
    if (url.startsWith('http')) return url;
    return `https://visioncareoptics.in/frontend/public${url}`;
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleHeroClick = () => navigate('/products');

  /* ── Auto-play ── */
  const startAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
  };

  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(autoPlayRef.current);
  }, [heroSlides.length]);

  /* ── Brands auto-scroll ── */
  useEffect(() => {
    const brandsContainer = brandsContainerRef.current;
    if (!brandsContainer || brands.length === 0) return;

    let scrollInterval;
    let isHovering = false;

    const startScrolling = () => {
      if (scrollInterval) clearInterval(scrollInterval);
      scrollInterval = setInterval(() => {
        if (!isHovering && brandsContainer) {
          brandsContainer.scrollLeft += 1;
          const maxScroll = brandsContainer.scrollWidth / 2;
          if (brandsContainer.scrollLeft >= maxScroll) brandsContainer.scrollLeft = 0;
        }
      }, 30);
    };

    const handleMouseEnter = () => { isHovering = true; };
    const handleMouseLeave = () => { isHovering = false; };

    brandsContainer.addEventListener('mouseenter', handleMouseEnter);
    brandsContainer.addEventListener('mouseleave', handleMouseLeave);
    startScrolling();

    return () => {
      if (scrollInterval) clearInterval(scrollInterval);
      if (brandsContainer) {
        brandsContainer.removeEventListener('mouseenter', handleMouseEnter);
        brandsContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [brands]);

  /* ── Data fetch ── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setBrands(defaultBrands);

        try {
          const categoriesRes = await fetch(`${API_BASE_URL}/categories`);
          if (categoriesRes.ok) {
            const categoriesData = await categoriesRes.json();
            setCategories(categoriesData.data || categoriesData.categories || categoriesData || []);
          } else {
            setCategories([]);
          }
        } catch {
          setCategories([]);
        }

        try {
          const productsRes = await fetch(`${API_BASE_URL}/products?featured=true`);
          if (productsRes.ok) {
            const productsData = await productsRes.json();
            const productsList = productsData.data || productsData.products || productsData || [];
            setFeaturedProducts(productsList);
            if (isAuthenticated && productsList.length > 0) checkAllWishlistStatus(productsList);
          } else {
            setFeaturedProducts([]);
          }
        } catch {
          setFeaturedProducts([]);
        }
      } catch (error) {
        console.error('Error in fetchData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  const checkAllWishlistStatus = async (products) => {
    if (!isAuthenticated) return;
    const status = {};
    for (const product of products) {
      try {
        const response = await fetch(`${API_BASE_URL}/wishlist/check/${product.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.ok) {
          const data = await response.json();
          status[product.id] = data.in_wishlist;
        }
      } catch (error) {
        console.error('Error checking wishlist:', error);
      }
    }
    setWishlistStatus(status);
  };

  const goToSlide = (index) => { setCurrentSlide(index); startAutoPlay(); };
  const nextSlide = () => { setCurrentSlide((prev) => (prev + 1) % heroSlides.length); startAutoPlay(); };
  const prevSlide = () => { setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length); startAutoPlay(); };

  const scrollLeft  = () => scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' });
  const scrollRight = () => scrollContainerRef.current?.scrollBy({ left:  300, behavior: 'smooth' });

  const handleAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/', message: 'Please login to add to cart' } });
      return;
    }
    const cartItem = {
      product_id:   product.id,
      product_name: product.name,
      quantity:     1,
      price:        product.sale_price || product.price,
      image:        getProductImageUrl(product.images),
    };
    try {
      await dispatch(addToCart(cartItem)).unwrap();
      showToast('Added to cart!');
    } catch {
      showToast('Error adding to cart', 'error');
    }
  };

  const handleWishlistToggle = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/', message: 'Please login to add to wishlist' } });
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          Authorization:   `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ product_id: product.id }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setWishlistStatus((prev) => ({ ...prev, [product.id]: data.in_wishlist }));
        showToast(data.message);
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      showToast('Error updating wishlist', 'error');
    }
  };

  const handleProductClick  = (productId) => navigate(`/products/${productId}`);

  const handleCategoryClick = (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/products?category=${encodeURIComponent(category.slug || category.name)}`);
  };

  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) { showToast('Please enter your email address', 'error'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) { showToast('Please enter a valid email address', 'error'); return; }

    setNewsletterLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showToast(data.message || 'Successfully subscribed!', 'success');
        setNewsletterEmail('');
      } else {
        showToast(data.message || 'Failed to subscribe', 'error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      showToast('Error subscribing. Please try again.', 'error');
    } finally {
      setNewsletterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="home-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading amazing eyewear...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">

      {/* ===== HERO CAROUSEL ===== */}
      <section className="hero-section">
        <div className="hero-aspect-box">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`hero-slide${index === currentSlide ? ' hero-slide--active' : ''}`}
              onClick={handleHeroClick}
              role="button"
              tabIndex={index === currentSlide ? 0 : -1}
              aria-label={`Slide ${index + 1} — Go to shop`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleHeroClick(); }}
            >
              <img
                src={slide.image}
                alt={`Banner ${index + 1}`}
                loading={index === 0 ? 'eager' : 'lazy'}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/1920x1080?text=Vision+Care+Optics';
                }}
              />
            </div>
          ))}

          {/* Prev arrow */}
          <button
            className="hero-arrow hero-arrow--prev"
            onClick={(e) => { e.stopPropagation(); prevSlide(); }}
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          {/* Next arrow */}
          <button
            className="hero-arrow hero-arrow--next"
            onClick={(e) => { e.stopPropagation(); nextSlide(); }}
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>

          {/* Dots */}
          <div className="hero-dots" role="tablist" aria-label="Slide navigation">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                role="tab"
                aria-selected={index === currentSlide}
                className={`hero-dot${index === currentSlide ? ' hero-dot--active' : ''}`}
                onClick={(e) => { e.stopPropagation(); goToSlide(index); }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== BRANDS ===== */}
      <section className="brands-section">
        <div className="container">
          <p className="brands-subtitle">TRUSTED BY PROFESSIONALS WORLDWIDE</p>
          <div className="brands-scroll-container">
            <div className="brands-track" ref={brandsContainerRef}>
              {[...brands, ...brands].map((brand, index) => (
                <div key={`${brand.id}-${index}`} className="brand-item">
                  <img
                    src={brand.logo}
                    alt={brand.alt}
                    className="brand-logo"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/150x80?text=' + encodeURIComponent(brand.name);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CATEGORIES ===== */}
      <section className="categories-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Shop by Category</h2>
            <p className="section-subtitle">Explore our wide range of eyewear and eye care products</p>
          </div>

          {categories.length === 0 ? (
            <div className="no-data-message">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
              </svg>
              <h3>No Categories Found</h3>
              <p>Please add categories to your database to see them here.</p>
            </div>
          ) : (
            <div className="categories-grid">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="category-card"
                  onClick={(e) => handleCategoryClick(e, category)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCategoryClick(e, category); }}
                >
                  <div className="category-image">
                    <img
                      src={resolveImageUrl(category.image_url)}
                      alt={category.name}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x400?text=' + encodeURIComponent(category.name);
                      }}
                    />
                    <div className="category-overlay" />
                  </div>
                  <div className="category-info">
                    <h3 className="category-name">{category.name}</h3>
                    <span className="category-count">{category.product_count || 0} styles</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      <section className="featured-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Featured Products</h2>
            <p className="section-subtitle">Hand-picked selections just for you</p>
          </div>

          {featuredProducts.length === 0 ? (
            <div className="no-data-message">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#95a5a6" strokeWidth="1.5">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <h3>No Featured Products</h3>
              <p>Mark some products as featured in your database to see them here.</p>
            </div>
          ) : (
            <div className="featured-products-grid">
              {featuredProducts.map((product) => {
                const productImage = getProductImageUrl(product.images);
                return (
                  <div
                    key={product.id}
                    className="product-card"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="product-thumb">
                      <img
                        src={productImage}
                        alt={product.name}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x400/f5f5f5/999999?text=' + encodeURIComponent(product.name || 'Product');
                        }}
                      />
                      {product.sale_price && <span className="badge-sale">SALE</span>}
                      <button
                        className={`btn-wishlist${wishlistStatus[product.id] ? ' btn-wishlist--active' : ''}`}
                        aria-label="Add to wishlist"
                        onClick={(e) => handleWishlistToggle(e, product)}
                      >
                        <svg viewBox="0 0 24 24" fill={wishlistStatus[product.id] ? '#e74c3c' : 'none'} stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </button>
                    </div>
                    <div className="product-info">
                      <span className="product-brand">{product.brand || 'Premium Brand'}</span>
                      <h3 className="product-name">{product.name}</h3>
                      <div className="product-price">
                        {product.sale_price ? (
                          <>
                            <span className="price-sale">{formatPrice(product.sale_price)}</span>
                            <span className="price-original">{formatPrice(product.price)}</span>
                          </>
                        ) : (
                          <span className="price-regular">{formatPrice(product.price)}</span>
                        )}
                      </div>
                      <button
                        className="btn-add-cart"
                        onClick={(e) => handleAddToCart(e, product)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="9" cy="21" r="1" />
                          <circle cx="20" cy="21" r="1" />
                          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                        <span>Add to Cart</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <h3 className="feature-title">Free Shipping</h3>
              <p className="feature-desc">On all orders over ₹999</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <path d="M20 12.5A7.5 7.5 0 0 1 8 15m0 0l3-3m-3 3l-3-3M4 7.5V4a1 1 0 0 1 1-1h4.5M20 7.5V4a1 1 0 0 0-1-1h-4.5" />
                </svg>
              </div>
              <h3 className="feature-title">Quality Assurance</h3>
              <p className="feature-desc">Every product passed the QC</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h3 className="feature-title">Secure Payment</h3>
              <p className="feature-desc">100% secure transactions</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="#3498db" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="2" />
                  <path d="M22 12c-2.667 4.667-6 7-10 7s-7.333-2.333-10-7c2.667-4.667 6-7 10-7s7.333 2.333 10 7z" />
                </svg>
              </div>
              <h3 className="feature-title">Virtual Try-On</h3>
              <p className="feature-desc">See how they look on you</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Customer Stories</h2>
            <p className="section-subtitle">Join thousands of satisfied customers</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t) => (
              <div key={t.id} className="testimonial-card">
                <div className="testimonial-stars">{'★'.repeat(t.rating)}{'☆'.repeat(5 - t.rating)}</div>
                <p className="testimonial-comment">{t.comment}</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{t.name.charAt(0)}</div>
                  <div className="author-info">
                    <h4>{t.name}</h4>
                    <span>{t.verified ? 'Verified Buyer' : 'Customer'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA / NEWSLETTER ===== */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">🎉 Welcome Offer</div>
            <h2 className="cta-title">New Here? Get ₹200 Off!</h2>
            <p className="cta-description">
              Subscribe to our newsletter and receive ₹200 off on your very first order. Style starts here.
            </p>
            <form className="cta-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email address"
                className="cta-input"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={newsletterLoading}
                required
              />
              <button type="submit" className="cta-button" disabled={newsletterLoading}>
                <span>{newsletterLoading ? 'Subscribing...' : 'Claim ₹200 Off'}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </form>
            <p className="cta-note">*Valid on first order only. Terms and conditions apply. Unsubscribe anytime.</p>
          </div>
        </div>
      </section>

      {/* ===== WHATSAPP FLOATING BUTTON ===== */}
      <a
        href="https://wa.me/919080909500"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat with us on WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="30" height="30" fill="white">
          <path d="M24 4C13 4 4 13 4 24c0 3.6 1 7 2.7 9.9L4 44l10.4-2.7C17.2 43 20.5 44 24 44c11 0 20-9 20-20S35 4 24 4zm0 36c-3.1 0-6.1-.8-8.7-2.4l-.6-.4-6.2 1.6 1.7-6-.4-.6C8.2 29.8 7.5 27 7.5 24c0-9.1 7.4-16.5 16.5-16.5S40.5 14.9 40.5 24 33.1 40 24 40zm9-12.4c-.5-.2-2.9-1.4-3.3-1.6-.4-.2-.8-.2-1.1.2s-1.3 1.6-1.6 2c-.3.3-.6.4-1.1.1-.5-.2-2-.7-3.8-2.3-1.4-1.2-2.3-2.7-2.6-3.2-.3-.5 0-.7.2-.9.2-.2.5-.5.7-.8.2-.3.3-.5.5-.8.2-.3.1-.6 0-.8-.1-.2-1.1-2.6-1.5-3.5-.4-.9-.8-.8-1.1-.8h-.9c-.3 0-.8.1-1.2.6-.4.5-1.6 1.5-1.6 3.7s1.6 4.3 1.8 4.6c.2.3 3.1 4.8 7.6 6.7 1.1.5 1.9.7 2.6.9 1.1.3 2 .3 2.8.2.9-.1 2.9-1.2 3.3-2.3.4-1.2.4-2.2.3-2.4-.1-.2-.5-.3-1-.5z" />
        </svg>
        <span className="whatsapp-tooltip">Chat with us</span>
      </a>

      {/* ===== TOAST ===== */}
      {toast && (
        <div className={`home-toast home-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default HomePage;